import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import CreateEventCompanyShortcut from "../CreateEvent/CreateEventCompanyShortcut.jsx";
import { formatDateRange } from "../../utils/formatDate.js";
import { getChampionshipStateMeta } from "../../utils/championshipState.js";
import {
  getApiErrorMessage,
  listCompanyEvents,
  listCompanySports,
} from "../../services/adminCompanyService.js";
import EventEditModal from "./EventEditModal.jsx";

const CompanyEventsTab = ({ idcompany, periodo }) => {
  const [events, setEvents] = useState([]);
  const [sports, setSports] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const loadEvents = useCallback(async () => {
    if (!idcompany || !periodo) return;
    setLoadingEvents(true);
    try {
      setEvents(await listCompanyEvents({ idcompany, periodo }));
    } catch (error) {
      console.error("Error al cargar eventos de la compañía", error);
      toast.error(getApiErrorMessage(error, "No se pudieron cargar los eventos."));
    } finally {
      setLoadingEvents(false);
    }
  }, [idcompany, periodo]);

  const loadSports = useCallback(async () => {
    if (!idcompany) return;
    try {
      setSports(await listCompanySports(idcompany));
    } catch (error) {
      console.error("Error al cargar deportes de la compañía", error);
      toast.error(
        getApiErrorMessage(error, "No se pudieron cargar los deportes de la compañía.")
      );
    }
  }, [idcompany]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadSports();
  }, [loadSports]);

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
        <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
              Eventos de la organización
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-300">
              Periodo {periodo}. Crea eventos o actualiza su información general.
            </p>
          </div>
          <div className="shrink-0">
            <CreateEventCompanyShortcut
              idcompany={idcompany}
              deportes={sports}
              onCreated={loadEvents}
            />
          </div>
        </div>

        {loadingEvents ? (
          <div className="py-10 text-center text-sm text-slate-500">
            Cargando eventos...
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
            No hay eventos registrados para esta compañía en el periodo seleccionado.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {events.map((event) => {
              const stateMeta = getChampionshipStateMeta(event.state);

              return (
                <article
                  key={event.idchampionship}
                  className="flex min-h-48 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                >
                  <div className="hidden h-auto w-36 shrink-0 items-center justify-center overflow-hidden bg-slate-50 sm:flex">
                    {event.image_path ? (
                      <img
                        src={event.image_path}
                        alt={event.name}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <span className="px-3 text-center text-xs text-slate-400">
                        Sin imagen
                      </span>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col p-4">
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-slate-800">{event.name}</h3>
                      <span
                        className={`shrink-0 rounded-full px-2 py-1 text-xs font-semibold ring-1 ${stateMeta.className}`}
                      >
                        {stateMeta.label}
                      </span>
                    </div>
                    <dl className="space-y-1 text-sm text-slate-500">
                      <div className="flex gap-2">
                        <dt className="font-medium text-slate-600">Lugar:</dt>
                        <dd>{event.place || "Sin registrar"}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="font-medium text-slate-600">Fechas:</dt>
                        <dd>{formatDateRange(event.startdate, event.enddate)}</dd>
                      </div>
                    </dl>
                    <div className="mt-auto flex justify-end pt-4">
                      <button
                        type="button"
                        onClick={() => setEditingEvent(event)}
                        className="rounded-lg border border-blue-200 px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-50"
                      >
                        Editar evento
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {editingEvent && (
        <EventEditModal
          event={editingEvent}
          idcompany={idcompany}
          onClose={() => setEditingEvent(null)}
          onSaved={loadEvents}
        />
      )}
    </div>
  );
};

export default CompanyEventsTab;
